import React, { useState, useCallback, useRef, useMemo } from "react";
import { MediaItem } from "@/core/MediaItem/MediaItem.model";
import { MediaStage } from "@/core/types/mediaTypes";
import { useImageActions } from "@/core/MediaItem/useImageActions";
import { UserRole } from "@/services/urlService";
import { copyToClipboard } from "@/utils/clipboard";
import { ShareIcon, LinkIcon } from "@/components/icons";
import { MenuItem } from "../ContextMenu";
import { useAppStore } from "@/core/store/useAppStore";
import { collectionItemCacheUtils } from "@/core/MediaLibrary/hooks/useCollectionItemsQuery";

export interface UseAdminMediaActionsProps {
  mediaItem: MediaItem;
  clientId: string;
  onStageChange?: (updatedItem: MediaItem, oldStage: MediaStage) => void;
  onMediaItemDeleted?: (id: number) => void;
  onEditClick?: (item: MediaItem) => void;
  onActionError?: (error: string) => void;
  onAddToCollection?: (mediaItemId: number) => void;
}

export interface UseAdminMediaActionsReturn {
  // State
  isLabelModalOpen: boolean;
  isReprocessing: boolean;
  isRotating: "clockwise" | "counterclockwise" | null;
  downloadStatus: 'checking' | 'downloading' | 'ready' | 'failed' | null;
  isRevisionModalOpen: boolean;

  // Handlers
  handleStageChange: (newStage: string) => Promise<void>;
  handleDownload: (e: React.MouseEvent) => Promise<void>;
  handleEdit: (e: React.MouseEvent) => void;
  handleLabelClick: (e: React.MouseEvent) => void;
  handleLabelUpdate: (updatedMediaItem: MediaItem) => void;
  setIsLabelModalOpen: (open: boolean) => void;
  setIsRevisionModalOpen: (open: boolean) => void;

  // Context menu
  contextMenuItems: MenuItem[];
}

/**
 * Hook for admin media card business logic
 *
 * Extracts all the complex state management and handlers from AdminMediaCard
 * so the MediaCard component can stay simple and composable.
 */
export function useAdminMediaActions({
  mediaItem,
  clientId,
  onStageChange,
  onMediaItemDeleted,
  onEditClick,
  onActionError,
  onAddToCollection,
}: UseAdminMediaActionsProps): UseAdminMediaActionsReturn {
  const mediaItemRef = useRef(mediaItem);
  mediaItemRef.current = mediaItem;

  const {
    collections: collectionsState,
    removeItemsFromActiveCollection,
  } = useAppStore();
  const { isCollectionMode, activeCollectionId } = collectionsState;

  const [isLabelModalOpen, setIsLabelModalOpen] = useState(false);
  const [isReprocessing, setIsReprocessing] = useState(false);
  const [isRotating, setIsRotating] = useState<"clockwise" | "counterclockwise" | null>(null);
  const [downloadStatus, setDownloadStatus] = useState<'checking' | 'downloading' | 'ready' | 'failed' | null>(null);
  const [compressedDownloadStatus, setCompressedDownloadStatus] = useState<'checking' | 'ready' | 'failed' | 'not_available' | null>(null);
  const [isRevisionModalOpen, setIsRevisionModalOpen] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isRemovingFromCollection, setIsRemovingFromCollection] = useState(false);

  const {
    isChangingStage,
    updateStage,
  } = useImageActions(clientId);

  const handleStageChange = useCallback(
    async (newStage: string) => {
      const currentMediaItem = mediaItemRef.current;
      // Story media is ALWAYS taken (its auto-created story wraps it), so the
      // taken-guard would make un-approval impossible from the card. The
      // backend cascade-cancels the story on un-approval (TD-029), so stage
      // changes stay safe for stories; the guard still protects feed media.
      if (
        currentMediaItem.isTaken &&
        currentMediaItem.getContentType() !== "stories"
      )
        return;

      // Use the ref directly - React Query keeps the cache fresh via SSE
      const oldStage = currentMediaItem.stage as MediaStage;

      if (currentMediaItem.stage === newStage) {
        console.log(`ℹ️  Media item ${currentMediaItem.getName()} already at stage ${newStage}`);
        return;
      }

      try {
        const updatedMediaItem = await updateStage(currentMediaItem, newStage);
        if (updatedMediaItem) {
          onStageChange?.(updatedMediaItem, oldStage);
          console.log(`✅ Media item ${currentMediaItem.getName()} stage updated to ${newStage}`);
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Failed to update stage";
        console.error("❌ Failed to update stage:", err);
        onActionError?.(errorMessage);
      }
    },
    [updateStage, onStageChange, onActionError]
  );

  const handleDownload = useCallback(
    async (e: React.MouseEvent) => {
      try {
        await mediaItemRef.current.downloadFile('admin', (status) => {
          setDownloadStatus(status);

          if (status === 'ready' || status === 'failed') {
            setTimeout(() => setDownloadStatus(null), 3000);
          }
        });
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Failed to download file";
        console.error("❌ Failed to download:", err);
        onActionError?.(errorMessage);
        setDownloadStatus('failed');
        setTimeout(() => setDownloadStatus(null), 3000);
      }
    },
    [onActionError]
  );

  const handleDownloadCompressed = useCallback(
    async (e: React.MouseEvent) => {
      try {
        await mediaItemRef.current.downloadCompressedFile(UserRole.ADMIN, (status) => {
          setCompressedDownloadStatus(status);

          if (status === 'ready' || status === 'failed' || status === 'not_available') {
            setTimeout(() => setCompressedDownloadStatus(null), 3000);
          }
        });
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Failed to download compressed file";
        console.error("Failed to download compressed:", err);
        onActionError?.(errorMessage);
        setCompressedDownloadStatus('failed');
        setTimeout(() => setCompressedDownloadStatus(null), 3000);
      }
    },
    [onActionError]
  );

  const handleEdit = useCallback(
    (e: React.MouseEvent) => {
      onEditClick?.(mediaItemRef.current);
    },
    [onEditClick]
  );

  const handleLabelClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsLabelModalOpen(true);
  }, []);

  const handleLabelUpdate = useCallback(
    (updatedMediaItem: MediaItem) => {
      const currentStage = updatedMediaItem.stage as MediaStage;
      onStageChange?.(updatedMediaItem, currentStage);
    },
    [onStageChange]
  );

  // Placeholder handlers for complex operations (implement based on AdminMediaCard)
  const handleReprocess = useCallback(async (e: React.MouseEvent) => {
    if (isReprocessing) return;

    setIsReprocessing(true);
    try {
      const { imageActionsService } = await import("@/core/MediaItem/ImageActionsService");
      const result = await imageActionsService.reprocessVideo(clientId, mediaItem.id);

      if (result.success) {
        console.log(`✅ Video queued for reprocessing: ${mediaItem.getName()}`);
        onActionError?.("Video queued for reprocessing. The page will refresh when processing completes.");

        if (onStageChange) {
          const updatedMediaItem = mediaItem.clone({
            processingStatus: "processing",
          });
          onStageChange(updatedMediaItem, mediaItem.stage as MediaStage);
        }
      } else {
        throw new Error(result.message || "Failed to reprocess video");
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to reprocess video";
      console.error("❌ Reprocess error:", error);
      onActionError?.(errorMessage);
    } finally {
      setTimeout(() => setIsReprocessing(false), 1000);
    }
  }, [isReprocessing, clientId, mediaItem, onStageChange, onActionError]);

  const handleReprocessImage = useCallback(async (e: React.MouseEvent) => {
    if (isReprocessing) return;

    setIsReprocessing(true);
    try {
      const { imageActionsService } = await import("@/core/MediaItem/ImageActionsService");
      const result = await imageActionsService.reprocessImage(clientId, mediaItem.id);

      if (result.success) {
        console.log(`✅ Image queued for reprocessing: ${mediaItem.getName()}`);
        onActionError?.("Image queued for reprocessing. The page will refresh when processing completes.");

        if (onStageChange) {
          const updatedMediaItem = mediaItem.clone({
            processingStatus: "processing",
          });
          onStageChange(updatedMediaItem, mediaItem.stage as MediaStage);
        }
      } else {
        throw new Error(result.error || "Failed to queue image for reprocessing");
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to reprocess image";
      console.error("❌ Failed to reprocess image:", err);
      onActionError?.(errorMessage);
    } finally {
      setIsReprocessing(false);
    }
  }, [clientId, mediaItem, isReprocessing, onActionError, onStageChange]);

  const handleRotateClockwise = useCallback(async (e: React.MouseEvent) => {
    if (isRotating) return;

    setIsRotating("clockwise");
    try {
      const { imageActionsService } = await import("@/core/MediaItem/ImageActionsService");
      const result = await imageActionsService.rotateVideo(clientId, mediaItem.id, "clockwise");

      if (result.success) {
        console.log(`✅ Video queued for clockwise rotation: ${mediaItem.getName()}`);
        onActionError?.("Video queued for rotation. The page will refresh when processing completes.");

        if (onStageChange) {
          const updatedMediaItem = mediaItem.clone({
            processingStatus: "processing",
          });
          onStageChange(updatedMediaItem, mediaItem.stage as MediaStage);
        }
      } else {
        throw new Error(result.error || "Failed to rotate video");
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to rotate video";
      console.error("❌ Failed to rotate video:", err);
      onActionError?.(errorMessage);
    } finally {
      setIsRotating(null);
    }
  }, [clientId, mediaItem, isRotating, onActionError, onStageChange]);

  const handleRotateCounterclockwise = useCallback(async (e: React.MouseEvent) => {
    if (isRotating) return;

    setIsRotating("counterclockwise");
    try {
      const { imageActionsService } = await import("@/core/MediaItem/ImageActionsService");
      const result = await imageActionsService.rotateVideo(clientId, mediaItem.id, "counterclockwise");

      if (result.success) {
        console.log(`✅ Video queued for counterclockwise rotation: ${mediaItem.getName()}`);
        onActionError?.("Video queued for rotation. The page will refresh when processing completes.");

        if (onStageChange) {
          const updatedMediaItem = mediaItem.clone({
            processingStatus: "processing",
          });
          onStageChange(updatedMediaItem, mediaItem.stage as MediaStage);
        }
      } else {
        throw new Error(result.error || "Failed to rotate video");
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to rotate video";
      console.error("❌ Failed to rotate video:", err);
      onActionError?.(errorMessage);
    } finally {
      setIsRotating(null);
    }
  }, [clientId, mediaItem, isRotating, onActionError, onStageChange]);

  const handleTriggerAiAnalysis = useCallback(async (e: React.MouseEvent) => {
    try {
      const { imageActionsService } = await import("@/core/MediaItem/ImageActionsService");
      const result = await imageActionsService.triggerAiAnalysis(clientId, mediaItem.id);

      if (result.success) {
        console.log(`✅ Image queued for AI analysis: ${mediaItem.getName()}`);
        onActionError?.("Image queued for AI analysis. Results will appear automatically when complete.");

        if (onStageChange) {
          const updatedMediaItem = mediaItem.clone({
            status: "processing_ai" as any,
          });
          onStageChange(updatedMediaItem, mediaItem.stage as MediaStage);
        }
      } else {
        throw new Error(result.error || "Failed to queue image for AI analysis");
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to trigger AI analysis";
      console.error("❌ Failed to trigger AI analysis:", err);
      onActionError?.(errorMessage);
    }
  }, [clientId, mediaItem, onActionError, onStageChange]);

  const handleMakePublic = useCallback(async () => {
    if (isPublishing) return;
    setIsPublishing(true);
    try {
      const { imageActionsService } = await import("@/core/MediaItem/ImageActionsService");
      const result = await imageActionsService.makePublic(clientId, mediaItem.id);
      await copyToClipboard(result.publicUrl);
      onActionError?.("Public link copied to clipboard.");
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to make public";
      console.error("Failed to make public:", err);
      onActionError?.(errorMessage);
    } finally {
      setIsPublishing(false);
    }
  }, [isPublishing, clientId, mediaItem, onActionError]);

  const handleCopyPublicLink = useCallback(async () => {
    try {
      const { imageActionsService } = await import("@/core/MediaItem/ImageActionsService");
      const result = await imageActionsService.makePublic(clientId, mediaItem.id);
      await copyToClipboard(result.publicUrl);
      onActionError?.("Public link copied to clipboard.");
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to copy public link";
      onActionError?.(errorMessage);
    }
  }, [clientId, mediaItem, onActionError]);

  const handleRemovePublic = useCallback(async () => {
    if (isPublishing) return;
    setIsPublishing(true);
    try {
      const { imageActionsService } = await import("@/core/MediaItem/ImageActionsService");
      await imageActionsService.removePublic(clientId, mediaItem.id);
      onActionError?.("Public link removed.");
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to remove public link";
      console.error("Failed to remove public link:", err);
      onActionError?.(errorMessage);
    } finally {
      setIsPublishing(false);
    }
  }, [isPublishing, clientId, mediaItem, onActionError]);

  const handleDelete = useCallback(async () => {
    if (!onMediaItemDeleted) return;

    try {
      await mediaItemRef.current.deleteCompletely();
      onMediaItemDeleted(mediaItemRef.current.id);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to delete media item";
      console.error("❌ Delete error:", error);
      onActionError?.(errorMessage);
    }
  }, [onMediaItemDeleted, onActionError]);

  const handleRemoveFromCollection = useCallback(async () => {
    if (!activeCollectionId || isRemovingFromCollection) return;

    setIsRemovingFromCollection(true);
    try {
      await removeItemsFromActiveCollection(clientId, [mediaItem.id]);
      collectionItemCacheUtils.invalidateCollectionItems(clientId, activeCollectionId);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to remove from collection";
      onActionError?.(errorMessage);
    } finally {
      setIsRemovingFromCollection(false);
    }
  }, [activeCollectionId, isRemovingFromCollection, removeItemsFromActiveCollection, clientId, mediaItem.id, onActionError]);

  // Context menu items
  const contextMenuItems = useMemo<MenuItem[]>(() => {
    const items: MenuItem[] = [];

    // Add to collection (normal view only)
    if (!isCollectionMode && onAddToCollection) {
      items.push({
        id: "add-to-collection",
        label: "Add to collection",
        loading: false,
        onClick: () => onAddToCollection(mediaItem.id),
        icon: (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path d="M7 3a1 1 0 000 2h6a1 1 0 100-2H7zM4 7a1 1 0 011-1h10a1 1 0 110 2H5a1 1 0 01-1-1zM2 11a2 2 0 012-2h12a2 2 0 012 2v4a2 2 0 01-2 2H4a2 2 0 01-2-2v-4z" />
          </svg>
        ),
      });
    }

    // Edit (if handler provided)
    if (mediaItem.isImage() && onEditClick) {
      items.push({
        id: "edit",
        label: "Edit",
        loading: false,
        onClick: () => handleEdit({} as React.MouseEvent),
        icon: (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
          </svg>
        ),
      });
    }

    // Video-specific actions
    if (mediaItem.isVideo()) {
      items.push({
        id: "rotate-clockwise",
        label: "Rotate clockwise",
        loading: isRotating === "clockwise",
        onClick: () => handleRotateClockwise({} as React.MouseEvent),
        icon: (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 12v8a2 2 0 002 2h8M4 12H1m3 0l3-3m6 0a6 6 0 110 12" />
          </svg>
        ),
      });

      items.push({
        id: "rotate-counterclockwise",
        label: "Rotate counterclockwise",
        loading: isRotating === "counterclockwise",
        onClick: () => handleRotateCounterclockwise({} as React.MouseEvent),
        icon: (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12v8a2 2 0 01-2 2h-8m8-12V4m0 8h3m-3 0l-3-3m-6 0a6 6 0 100 12" />
          </svg>
        ),
      });

      const contentType = mediaItem.getContentType();
      if (contentType === "videos" || contentType === "reels") {
        items.push({
          id: "reprocess-video",
          label: "Reprocess video",
          loading: isReprocessing,
          onClick: () => handleReprocess({} as React.MouseEvent),
          icon: (
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          ),
        });
      }
    }

    // Image-specific actions (photos only)
    if (mediaItem.isImage() && mediaItem.getContentType() === "photos") {
      if (!mediaItem.backendData.aiAnalysis) {
        items.push({
          id: "ai-analysis",
          label: "AI Analysis",
          loading: false,
          onClick: () => handleTriggerAiAnalysis({} as React.MouseEvent),
          icon: (
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
          ),
        });
      }

      items.push({
        id: "reprocess-image",
        label: "Reprocess image",
        loading: isReprocessing,
        onClick: () => handleReprocessImage({} as React.MouseEvent),
        variant: "danger",
        icon: (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        ),
      });
    }

    // Upload revision (only if not assigned to a post)
    if (!mediaItem.isTaken) {
      items.push({
        id: "upload-revision",
        label: "Upload revision",
        loading: false,
        onClick: () => setIsRevisionModalOpen(true),
        icon: (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
          </svg>
        ),
      });
    }

    // Download
    const downloadLabel = downloadStatus === 'checking' ? 'Checking file...' :
                         downloadStatus === 'downloading' ? 'Preparing download...' :
                         downloadStatus === 'ready' ? 'Opening download...' :
                         downloadStatus === 'failed' ? 'Download failed' :
                         'Download original';

    items.push({
      id: "download",
      label: downloadLabel,
      loading: downloadStatus === 'checking' || downloadStatus === 'downloading',
      onClick: () => handleDownload({} as React.MouseEvent),
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
        </svg>
      ),
    });

    // Download compressed version (optimized WebP for images, proxy MP4 for videos)
    const compressedLabel = compressedDownloadStatus === 'checking' ? 'Checking file...' :
                           compressedDownloadStatus === 'ready' ? 'Opening download...' :
                           compressedDownloadStatus === 'failed' ? 'Download failed' :
                           compressedDownloadStatus === 'not_available' ? 'Not yet processed' :
                           mediaItem.isVideo() ? 'Download proxy' : 'Download optimized';

    items.push({
      id: "download-compressed",
      label: compressedLabel,
      loading: compressedDownloadStatus === 'checking',
      onClick: () => handleDownloadCompressed({} as React.MouseEvent),
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
        </svg>
      ),
    });

    // Public sharing
    const publicStatus = mediaItem.publicStatus;
    if (publicStatus === "copying") {
      items.push({
        id: "make-public",
        label: "Copying to public...",
        loading: true,
        disabled: true,
        onClick: () => {},
        icon: <ShareIcon className="w-4 h-4" />,
      });
    } else if (publicStatus === "public") {
      items.push({
        id: "copy-public-link",
        label: "Copy public link",
        loading: false,
        onClick: handleCopyPublicLink,
        icon: <LinkIcon className="w-4 h-4" />,
      });
      items.push({
        id: "remove-public",
        label: "Remove public link",
        loading: isPublishing,
        onClick: handleRemovePublic,
        variant: "danger",
        icon: <ShareIcon className="w-4 h-4" />,
      });
    } else {
      items.push({
        id: "make-public",
        label: "Make public",
        loading: isPublishing,
        onClick: handleMakePublic,
        icon: <ShareIcon className="w-4 h-4" />,
      });
    }

    // Remove from collection (collection view only)
    if (isCollectionMode && activeCollectionId) {
      items.push({
        id: "remove-from-collection",
        label: "Remove from collection",
        loading: isRemovingFromCollection,
        onClick: handleRemoveFromCollection,
        variant: "danger",
        icon: (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        ),
      });
    }

    // Delete
    items.push({
      id: "delete",
      label: "Delete",
      loading: false,
      onClick: handleDelete,
      variant: "danger",
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
      ),
    });

    return items;
  }, [
    mediaItem,
    isReprocessing,
    isRotating,
    downloadStatus,
    compressedDownloadStatus,
    onEditClick,
    handleEdit,
    handleReprocess,
    handleReprocessImage,
    handleRotateClockwise,
    handleRotateCounterclockwise,
    handleTriggerAiAnalysis,
    handleDownload,
    handleDownloadCompressed,
    handleMakePublic,
    handleCopyPublicLink,
    handleRemovePublic,
    isPublishing,
    handleDelete,
    isCollectionMode,
    activeCollectionId,
    isRemovingFromCollection,
    handleRemoveFromCollection,
    onAddToCollection,
  ]);

  return {
    isLabelModalOpen,
    isReprocessing,
    isRotating,
    downloadStatus,
    isRevisionModalOpen,
    handleStageChange,
    handleDownload,
    handleEdit,
    handleLabelClick,
    handleLabelUpdate,
    setIsLabelModalOpen,
    setIsRevisionModalOpen,
    contextMenuItems,
  };
}
